import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    ColorPicker,
    Select,
    Segmented,
    Space,
    Tooltip,
} from 'antd';
import { UndoOutlined } from '@ant-design/icons';
import type { Color } from 'antd/es/color-picker';
import {
    ColorHarmony,
    ColorPaletteConfig,
    ColorWithTones,
    HARMONY_COLOR_COUNT_RULES,
    HARMONY_LABELS,
} from '@theme/types/colorHarmony.types.ts';
import { generateHarmony } from '@theme/utils/colorHarmony.ts';
import { normalizeHex } from '@theme/utils/colorConversions.ts';
import {
    generateTones,
    validatePaletteContrast,
} from '@theme/utils/colorTones.ts';



const DEFAULT_CONFIG: ColorPaletteConfig = {
    harmony: 'monochromatic',
    colorCount: 1,
    baseColor1: '#1677FF',
};

const HARMONY_TOOLTIPS: Record<ColorHarmony, string> = {
    monochromatic: 'Variaciones del mismo tono con cambios en luz y saturación.',
    analogous: 'Colores vecinos (±30°) para combinaciones suaves.',
    complementary: 'Colores opuestos (180°) para alto contraste.',
    triadic: 'Tres tonos equidistantes (120°) para balance vibrante.',
    custom: 'Seleccioná tus colores manualmente.',
};

/** Tonalidades a mostrar en columnas (sin base, que ya se muestra arriba). */
const TONE_VARIANTS_FOR_COLUMNS = ['c1', 'c2', 'o2', 'o1'] as const;
const TONE_COLUMN_LABELS: Record<(typeof TONE_VARIANTS_FOR_COLUMNS)[number], string> = {
    c1: 'Muy claro',
    c2: 'Claro',
    o2: 'Oscuro',
    o1: 'Muy oscuro',
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers para reglas de cantidad de colores
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Devuelve las opciones de colorCount disponibles para una armonía.
 * Si es `fixed`, retorna null (selector oculto).
 */
const getValidColorCountOptions = (
    harmony: ColorHarmony,
): (1 | 2 | 3)[] | null => {
    const rule = HARMONY_COLOR_COUNT_RULES[harmony];
    if ('fixed' in rule) return null;
    return rule.options;
};

/**
 * Devuelve el colorCount por defecto para una armonía.
 */
// @ts-ignore
const getDefaultColorCount = (harmony: ColorHarmony): 1 | 2 | 3 => {
    const rule = HARMONY_COLOR_COUNT_RULES[harmony];
    if ('fixed' in rule) return rule.fixed;
    return rule.default;
};

/**
 * Ajusta colorCount a un valor válido para la armonía.
 */
const clampColorCount = (
    harmony: ColorHarmony,
    currentCount: 1 | 2 | 3,
): 1 | 2 | 3 => {
    const rule = HARMONY_COLOR_COUNT_RULES[harmony];
    if ('fixed' in rule) return rule.fixed;
    if (rule.options.includes(currentCount)) return currentCount;
    return rule.default;
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componentes
// ─────────────────────────────────────────────────────────────────────────────

interface ColorCountSelectorProps {
    value: 1 | 2 | 3;
    options: (1 | 2 | 3)[];
    onChange: (value: 1 | 2 | 3) => void;
}

const ColorCountSelector = ({ value, options, onChange }: ColorCountSelectorProps) => (
    <Segmented
        block
        size="large"
        options={options.map((opt) => ({
            label: opt === 1 ? '1 color' : `${opt} colores`,
            value: opt,
        }))}
        value={value}
        onChange={(next) => onChange(next as 1 | 2 | 3)}
    />
);

interface HarmonyTypeSelectorProps {
    value: ColorHarmony;
    onChange: (value: ColorHarmony) => void;
}

const HarmonyTypeSelector = ({ value, onChange }: HarmonyTypeSelectorProps) => (
    <Select
        value={value}
        onChange={onChange}
        style={{ width: '100%' }}
        options={(Object.keys(HARMONY_LABELS) as ColorHarmony[]).map((key) => ({
            label: HARMONY_LABELS[key],
            value: key,
            title: HARMONY_TOOLTIPS[key],
        }))}
    />
);



// ─────────────────────────────────────────────────────────────────────────────
// Preview actualizado: bases en fila + tonos en columnas
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Preview actualizado: bases en fila + tonos en columnas
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Componente de Trigger individual para el ColorPicker
// ─────────────────────────────────────────────────────────────────────────────

interface PickerTriggerProps {
    color: string;
    index: number;
    isGhosted: boolean;
    onColorChange: (index: number, color: string) => void;
    onLivePreviewColor?: (index: number, color: string) => void;
}

const PickerTrigger = ({
    color,
    index,
    isGhosted,
    onColorChange,
    onLivePreviewColor,
}: PickerTriggerProps) => {
    const [internalColor, setInternalColor] = useState(color);
    const latestColorRef = useRef(color);
    const originalColorRef = useRef(color); // Store the original color when picker opens
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setInternalColor(color);
            latestColorRef.current = color;
        }
    }, [color, isOpen]);

    const handleChange = (val: Color) => {
        const hex = val.toHexString().toUpperCase();
        // Update local state and ref immediately for responsiveness
        latestColorRef.current = hex;
        if (hex !== internalColor) {
            setInternalColor(hex);
            // Live preview on drag!
            onLivePreviewColor?.(index, hex);
        }
    };

    const handleChangeComplete = (val: Color) => {
        const hex = val.toHexString().toUpperCase();
        latestColorRef.current = hex;
        // Trigger live preview only when interaction settles (click or drag end)
        onLivePreviewColor?.(index, hex);
    };

    const handleOpenChange = (open: boolean) => {
        if (open) {
            // Store the original color when picker opens
            originalColorRef.current = color;
        }

        setIsOpen(open);

        // When picker closes (user clicked outside or closed it)
        // We commit the latest color we have tracked.
        if (!open) {
            const finalColor = latestColorRef.current;
            const originalColor = originalColorRef.current;
            // Compare against the ORIGINAL color when picker opened, not current prop
            if (finalColor !== originalColor) {
                onColorChange(index, finalColor);
            }
        }
    };

    return (
        <ColorPicker
            value={internalColor}
            onChange={handleChange}
            onChangeComplete={handleChangeComplete}
            onOpenChange={handleOpenChange}
            disabled={false}
        >
            <div
                style={{
                    width: 56,
                    height: 56,
                    borderRadius: 10,
                    backgroundColor: internalColor,
                    border: isGhosted ? '2px dashed rgba(0,0,0,0.4)' : '2px solid rgba(0,0,0,0.08)',
                    boxShadow: isGhosted ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
                    // opacity: isGhosted ? 0.3 : 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {isGhosted && (
                    <div style={{ fontSize: 18, fontWeight: 'bold', color: 'rgba(0,0,0,0.2)' }}>
                        +
                    </div>
                )}
            </div>
        </ColorPicker>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Preview actualizado: bases en fila + tonos en columnas
// ─────────────────────────────────────────────────────────────────────────────

interface PalettePreviewProps {
    palette: ColorWithTones[];
    harmony: ColorHarmony;
    onColorChange: (index: number, color: string) => void;
    onLivePreviewColor?: (index: number, color: string) => void;
}

const PalettePreview = ({ palette, harmony, onColorChange, onLivePreviewColor }: PalettePreviewProps) => {
    const colorCount = palette.length;
    const isCustom = harmony === 'custom';

    return (
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            {/* Fila de colores base — cuadros grandes */}
            <div
                style={{
                    display: 'flex',
                    gap: 8,
                    justifyContent: 'center',
                }}
            >
                {palette.map((tones, idx) => {
                    // El primer color siempre es sólido.
                    // Los demás son 'ghosted' si NO estamos en modo custom.
                    const isGhosted = idx > 0 && !isCustom;

                    return (
                        <Tooltip key={idx} title={isGhosted ? 'Click para personalizar' : tones.base}>
                            <div>
                                <PickerTrigger
                                    color={tones.base}
                                    index={idx}
                                    isGhosted={isGhosted}
                                    onColorChange={onColorChange}
                                    onLivePreviewColor={onLivePreviewColor}
                                />
                            </div>
                        </Tooltip>
                    );
                })}
            </div>


            {/* Columnas de tonos (4 filas: c1, c2, o2, o1) */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${colorCount}, 1fr)`,
                    gap: 8,
                }}
            >
                {TONE_VARIANTS_FOR_COLUMNS.map((variant) =>
                    palette.map((tones, colIdx) => (
                        <Tooltip
                            key={`${variant}-${colIdx}`}
                            title={`${TONE_COLUMN_LABELS[variant]}: ${tones[variant]}`}
                        >
                            <div
                                style={{
                                    height: 28,
                                    borderRadius: 4,
                                    backgroundColor: tones[variant],
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    opacity: (colIdx > 0 && !isCustom) ? 0.5 : 1, // También atenuar derivadas
                                }}
                            />
                        </Tooltip>
                    )),
                )}
            </div>

            {/*/!* Indicadores de contraste *!/*/}
            {/*<Space wrap size={[8, 8]} style={{ justifyContent: 'center' }}>*/}
            {/*    {palette.map((tones, idx) => (*/}
            {/*        <Tag key={idx} color="default">*/}
            {/*            Color {idx + 1} — texto claro: {tones.contrastOnLight}*/}
            {/*        </Tag>*/}
            {/*    ))}*/}
            {/*</Space>*/}
        </Space>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

export interface ColorHarmonyPickerProps {
    value?: ColorPaletteConfig;
    onChange?: (value: ColorPaletteConfig) => void;
    /** Called on every color change for live preview (does not save) */
    onLivePreview?: (value: ColorPaletteConfig) => void;
    onApply?: (value: ColorPaletteConfig) => void;
    onReset?: () => void;
}

export const ColorHarmonyPicker = ({
    value,
    onChange,
    onLivePreview,
    onApply,
    onReset,
}: ColorHarmonyPickerProps) => {
    const [localConfig, setLocalConfig] = useState<ColorPaletteConfig>(value ?? DEFAULT_CONFIG);
    // Estado provisional para visualización en vivo (drag) sin commitear
    const [previewConfig, setPreviewConfig] = useState<ColorPaletteConfig | null>(null);

    // Solo sincronizar con el padre si el valor realmente cambió externamente
    // (no si el cambio vino de nosotros mismos via onChange)
    useEffect(() => {
        if (!value) return;

        // Comparar por valor, no por referencia, para evitar resets innecesarios
        const valueStr = JSON.stringify(value);
        const localStr = JSON.stringify(localConfig);

        // Solo actualizar si el valor externo es diferente del local
        if (valueStr !== localStr) {
            setLocalConfig(value);
            setPreviewConfig(null);
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    // Configuración activa para renderizado (prioriza preview durante interacción)
    const activeConfig = previewConfig || localConfig;

    const updateConfig = (patch: Partial<ColorPaletteConfig>) => {
        const next = { ...localConfig, ...patch };
        setLocalConfig(next);
        setPreviewConfig(null); // Limpiar preview al confirmar
        onChange?.(next);
        // Call onApply to update the saveable palette state in parent
        onApply?.(next);
        // Also update live preview when confirming
        onLivePreview?.(next);
    };

    // Al cambiar la armonía, ajustar colorCount según las nuevas reglas
    // IMPORTANTE: Preservar el color principal (baseColor1) para mejor UX
    const handleHarmonyChange = (harmony: ColorHarmony) => {
        const newCount = clampColorCount(harmony, localConfig.colorCount);
        // Mantener el baseColor1 actual al cambiar de armonía
        updateConfig({
            harmony,
            colorCount: newCount,
            baseColor1: localConfig.baseColor1, // Preservar explícitamente
        });
    };

    const colorCountOptions = getValidColorCountOptions(localConfig.harmony);

    const baseColors = useMemo(() => {
        // En modo CUSTOM, usamos los colores guardados
        if (activeConfig.harmony === 'custom') {
            const customColors = [activeConfig.baseColor1, activeConfig.baseColor2, activeConfig.baseColor3]
                .filter((color): color is string => Boolean(color?.trim()))
                .map((color) => normalizeHex(color));

            // Si falta algún color (ej. al pasar de 1 a 3), rellenamos o usamos defaults
            // Pero para visualización correcta, mejor asegurar que existen
            const safeColors = [...customColors];
            if (safeColors.length < activeConfig.colorCount) {
                // Rellenar con defaults si faltan
                if (safeColors.length < 1) safeColors.push('#1677FF');
                if (safeColors.length < 2) safeColors.push('#52C41A');
                if (safeColors.length < 3) safeColors.push('#FAAD14');
            }

            return safeColors.slice(0, activeConfig.colorCount);
        }

        // En otros modos, generamos baseColors matemáticamente
        // Pero necesitamos mostrarlos en el UI.
        return generateHarmony(activeConfig.baseColor1, activeConfig.harmony, activeConfig.colorCount);
    }, [
        activeConfig.baseColor1,
        activeConfig.baseColor2,
        activeConfig.baseColor3,
        activeConfig.colorCount,
        activeConfig.harmony,
    ]);

    const palette = useMemo(() => baseColors.map(generateTones), [baseColors]);
    const contrastStatus = useMemo(() => validatePaletteContrast(palette), [palette]);

    const handlePaletteColorChange = (index: number, newColor: string) => {
        if (index === 0) {
            // Cambio en color principal:
            // - Si es custom, actualiza baseColor1
            // - Si es armonía, actualiza baseColor1 y recalcula el resto (mantiene armonía)
            updateConfig({ baseColor1: newColor });
        } else {
            // Cambio en colores secundarios (index 1 o 2)

            if (localConfig.harmony !== 'custom') {
                // Si estaba en modo armonía, CAMBIAMOS A CUSTOM
                // y seteamos los colores actuales como punto de partida
                const currentColors = [...baseColors]; // Estos son los generados por la armonía actual
                currentColors[index] = newColor; // Actualizamos el que tocó el usuario

                updateConfig({
                    harmony: 'custom',
                    baseColor1: currentColors[0],
                    baseColor2: currentColors[1],
                    baseColor3: currentColors[2],
                });
            } else {
                // Ya estaba en custom, solo actualizamos el slot correspondiente
                const patch: Partial<ColorPaletteConfig> = {};
                if (index === 1) patch.baseColor2 = newColor;
                if (index === 2) patch.baseColor3 = newColor;
                updateConfig(patch);
            }
        }
    };

    // Handler for live preview while dragging color picker (doesn't update state)
    const handleLivePreviewColor = (index: number, newColor: string) => {
        let tempConfig: ColorPaletteConfig;
        if (index === 0) {
            tempConfig = { ...localConfig, baseColor1: newColor };
        } else if (localConfig.harmony !== 'custom') {
            // Simulate switching to custom mode
            const currentColors = [...baseColors];
            currentColors[index] = newColor;
            tempConfig = {
                ...localConfig,
                harmony: 'custom',
                baseColor1: currentColors[0],
                baseColor2: currentColors[1],
                baseColor3: currentColors[2],
            };
        } else {
            const patch: Partial<ColorPaletteConfig> = {};
            if (index === 1) patch.baseColor2 = newColor;
            if (index === 2) patch.baseColor3 = newColor;
            tempConfig = { ...localConfig, ...patch };
        }

        setPreviewConfig(tempConfig);
        onLivePreview?.(tempConfig);
    };

    return (
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small" title="Configuración de armonía" style={{ width: '100%' }}>
                <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                    <HarmonyTypeSelector
                        value={localConfig.harmony}
                        onChange={handleHarmonyChange}
                    />
                    {colorCountOptions && (
                        <ColorCountSelector
                            value={localConfig.colorCount}
                            options={colorCountOptions}
                            onChange={(count) => updateConfig({ colorCount: count })}
                        />
                    )}
                </Space>
            </Card>

            {!contrastStatus.valid && (
                <Alert
                    type="warning"
                    title="Contraste bajo"
                    description={contrastStatus.issues[0]}
                    showIcon
                />
            )}

            <Card
                size="small"
                title={
                    <Space size={8}>
                        <span>Preview de paleta</span>
                        {onReset && (
                            <Tooltip title="Resetear colores">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<UndoOutlined />}
                                    onClick={() => onReset()}
                                    style={{ color: 'inherit' }}
                                />
                            </Tooltip>
                        )}
                    </Space>
                }
                style={{ width: '100%' }}
            >
                <PalettePreview
                    palette={palette}
                    harmony={localConfig.harmony}
                    onColorChange={handlePaletteColorChange}
                    onLivePreviewColor={handleLivePreviewColor}
                />
            </Card>
        </Space>
    );
};
